import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
  Card,
  Tooltip,
  Snackbar,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Box,
} from "@mui/material";
import {
  Search,
  Refresh,
  Person,
  PictureAsPdf,
  Close,
  CalendarToday,
  AccessTime,
  AccountBalance,
  PointOfSale,
  Receipt,
  Timer,
  Calculate,
  Visibility,
  FilterAlt,
  Clear,
} from "@mui/icons-material";
import PropTypes from "prop-types";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";
import { getCierreTurnoDataService } from "modules/came/services/userCameService";
import { generarPDFCierreTurno } from "../Form/GeneradorPDFCierre";

// ==================== COMPONENTES AUXILIARES ====================

const IdCell = ({ value }) => (
  <MDTypography variant="caption" fontWeight="bold" color="info">
    #{value}
  </MDTypography>
);

IdCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const FechaCell = ({ fecha }) => {
  if (!fecha || fecha === "N/A") return <MDTypography variant="caption">N/A</MDTypography>;

  return (
    <MDBox>
      <MDTypography variant="caption" display="block" fontWeight="medium" sx={{ color: "#1e293b" }}>
        {formatDate(fecha)}
      </MDTypography>
      <MDTypography variant="caption" display="block" sx={{ color: "#64748b" }}>
        {formatTime(fecha)}
      </MDTypography>
    </MDBox>
  );
};

FechaCell.propTypes = {
  fecha: PropTypes.string,
};

const DuracionCell = ({ duracion }) => (
  <Chip
    icon={<Timer sx={{ fontSize: 14 }} />}
    label={duracion}
    size="small"
    variant="outlined"
    sx={{
      fontSize: "0.7rem",
      borderColor: "#cbd5e1",
      color: "#475569",
      bgcolor: "#f8fafc",
    }}
  />
);

DuracionCell.propTypes = {
  duracion: PropTypes.string,
};

const AccionesCell = ({ itemId, downloading, onDownload, onViewDetails }) => (
  <MDBox display="flex" gap={0.5}>
    <Tooltip title="Ver detalles">
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(itemId);
        }}
        sx={{
          color: "#546e7a",
          "&:hover": {
            backgroundColor: "#eceff1",
            color: "#37474f",
          },
        }}
      >
        <Visibility fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Descargar PDF">
      <IconButton
        size="small"
        color="error"
        onClick={(e) => {
          e.stopPropagation();
          onDownload(itemId);
        }}
        disabled={downloading}
        sx={{
          color: "#b71c1c",
          "&:hover": {
            backgroundColor: "#ffebee",
          },
        }}
      >
        {downloading ? <CircularProgress size={18} /> : <PictureAsPdf fontSize="small" />}
      </IconButton>
    </Tooltip>
  </MDBox>
);

AccionesCell.propTypes = {
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  downloading: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

// ==================== MODAL DE DETALLES ====================

const DetailsModal = ({ open, item, onClose, downloading, onDownload }) => {
  if (!item) return null;

  const metadata = item.metadata || item.more || {};
  const balance = metadata.balance || {};
  const trans = metadata.trans || {};

  const fechaInicioTurno = balance.FHInicio;
  const fechaFinTurno = balance.FHFin;
  const duracionTurno = calcularDuracion(fechaInicioTurno, fechaFinTurno);

  const efectivoInicial = Number(metadata.efectivoInicial || balance.EstContableAnterior || 0);
  const recaudoEfectivoReportado = Number(metadata.recaudoEfectivo || 0);
  const efectivoSistema = Number(trans.valorE || 0);
  const tarjetaSistema = Number(trans.valorT || 0);
  const valorTotalSistema = efectivoSistema + tarjetaSistema;
  const diferenciaEfectivo = recaudoEfectivoReportado - efectivoSistema;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "90vh",
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: "#1e293b", color: "white" }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox display="flex" alignItems="center" gap={1}>
            <Receipt sx={{ fontSize: 28 }} />
            <MDTypography variant="h5" color="white" fontWeight="bold">
              Detalles del Cierre #{item.id}
            </MDTypography>
          </MDBox>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <Close />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, bgcolor: "#f8fafc" }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                p: 2,
                bgcolor: "#334155",
                color: "white",
                borderRadius: "8px",
                boxShadow: "none",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Timer sx={{ color: "#94a3b8" }} />
                    <MDBox>
                      <MDTypography variant="caption" sx={{ color: "#cbd5e1" }}>
                        Duración del Turno
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold" color="white">
                        {duracionTurno}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <AccessTime sx={{ color: "#94a3b8" }} />
                    <MDBox>
                      <MDTypography variant="caption" sx={{ color: "#cbd5e1" }}>
                        Inicio del Turno
                      </MDTypography>
                      <MDTypography variant="body2" color="white">
                        {formatDateTime(fechaInicioTurno)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <AccessTime sx={{ color: "#94a3b8" }} />
                    <MDBox>
                      <MDTypography variant="caption" sx={{ color: "#cbd5e1" }}>
                        Fin del Turno
                      </MDTypography>
                      <MDTypography variant="body2" color="white">
                        {formatDateTime(fechaFinTurno)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2,
                height: "100%",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <MDTypography
                variant="subtitle2"
                sx={{ color: "#1e293b", fontWeight: 600 }}
                gutterBottom
              >
                <Person sx={{ mr: 1, fontSize: 18, color: "#546e7a", verticalAlign: "middle" }} />
                Información General
              </MDTypography>
              <Divider sx={{ my: 1.5, borderColor: "#e2e8f0" }} />

              <MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    ID:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    #{item.id}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Usuario:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    {item.user_kme || metadata.user_kme}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Caja:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    Caja #{balance.NumeroCaja || "N/A"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2,
                height: "100%",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <MDTypography
                variant="subtitle2"
                sx={{ color: "#1e293b", fontWeight: 600 }}
                gutterBottom
              >
                <PointOfSale
                  sx={{ mr: 1, fontSize: 18, color: "#546e7a", verticalAlign: "middle" }}
                />
                Datos de Caja
              </MDTypography>
              <Divider sx={{ my: 1.5, borderColor: "#e2e8f0" }} />

              <MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Número Turno:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    #{balance.NumeroTurno || "N/A"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2,
                height: "100%",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <MDTypography
                variant="subtitle2"
                sx={{ color: "#1e293b", fontWeight: 600 }}
                gutterBottom
              >
                <Calculate
                  sx={{ mr: 1, fontSize: 18, color: "#546e7a", verticalAlign: "middle" }}
                />
                Resumen Financiero
              </MDTypography>
              <Divider sx={{ my: 1.5, borderColor: "#e2e8f0" }} />

              <MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Efectivo Inicial:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#0b4f6c" }}>
                    {formatCurrency(efectivoInicial)}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Recaudo Efectivo:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    {formatCurrency(recaudoEfectivoReportado)}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Efectivo Sistema:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    {formatCurrency(efectivoSistema)}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }} fontWeight="bold">
                    Diferencia:
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    sx={{ color: diferenciaEfectivo >= 0 ? "#2e7d32" : "#d32f2f" }}
                  >
                    {formatCurrency(diferenciaEfectivo)}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Tarjeta Sistema:
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1e293b" }}>
                    {formatCurrency(tarjetaSistema)}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={1.5}>
                  <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                    Total Sistema (Efectivo + Tarjeta):
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight="bold" sx={{ color: "#1976d2" }}>
                    {formatCurrency(valorTotalSistema)}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between", bgcolor: "#f1f5f9" }}>
        <MDButton
          variant="outlined"
          color="error"
          startIcon={<PictureAsPdf />}
          onClick={() => onDownload(item.id)}
          disabled={downloading}
          sx={{
            borderRadius: "6px",
            borderColor: "#b71c1c",
            color: "#b71c1c",
            "&:hover": {
              borderColor: "#8b0000",
              backgroundColor: "#ffebee",
            },
          }}
        >
          {downloading ? "Descargando..." : "Descargar PDF"}
        </MDButton>
        <MDButton
          variant="contained"
          onClick={onClose}
          sx={{
            borderRadius: "6px",
            bgcolor: "#1e293b",
            "&:hover": {
              bgcolor: "#334155",
            },
          }}
        >
          Cerrar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

DetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  downloading: PropTypes.bool,
  onDownload: PropTypes.func.isRequired,
};

// ==================== FUNCIONES DE UTILIDAD ====================

const formatDateTime = (dateString) => {
  if (!dateString || dateString === "N/A") return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return dateString;
  }
};

const formatDate = (dateString) => {
  if (!dateString || dateString === "N/A") return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return dateString;
  }
};

const formatTime = (dateString) => {
  if (!dateString || dateString === "N/A") return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "UTC",
    });
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount) => {
  const num = Number(amount || 0);
  if (isNaN(num)) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const calcularDuracion = (inicio, fin) => {
  if (!inicio || !fin || inicio === "N/A" || fin === "N/A") return "N/A";
  try {
    const start = new Date(inicio);
    const end = new Date(fin);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    const seconds = Math.floor((diffMs % 60000) / 1000);

    if (hours > 0) {
      return `${hours}h ${mins}m ${seconds}s`;
    } else if (mins > 0) {
      return `${mins}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  } catch {
    return "N/A";
  }
};

// ==================== COMPONENTE PRINCIPAL ====================

function TableCameContent({ refreshFlag, onError }) {
  const [cierreData, setCierreData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const getFechaInicioTurnoData = (item) => {
    try {
      const metadata = item.metadata || item.more || {};
      const balance = metadata.balance || {};
      const fechaInicio = balance.FHInicio;

      if (!fechaInicio || fechaInicio === "N/A") return { dateStr: null, timestamp: 0 };

      const date = new Date(fechaInicio);
      if (isNaN(date.getTime())) return { dateStr: null, timestamp: 0 };

      return {
        dateStr: date.toISOString().split("T")[0],
        timestamp: date.getTime(),
      };
    } catch {
      return { dateStr: null, timestamp: 0 };
    }
  };

  const sortByFechaInicioTurno = (data) => {
    return [...data].sort((a, b) => {
      const fechaA = getFechaInicioTurnoData(a).timestamp;
      const fechaB = getFechaInicioTurnoData(b).timestamp;
      return fechaB - fechaA;
    });
  };

  const applyFilters = (data) => {
    let result = [...data];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((item) => {
        const searchableFields = [
          String(item.id || ""),
          String(item.user_kme || "").toLowerCase(),
          String(item.user_gp || "").toLowerCase(),
        ];
        return searchableFields.some((field) => field.includes(query));
      });
    }

    if (startDate || endDate) {
      result = result.filter((item) => {
        const { dateStr: fechaInicio } = getFechaInicioTurnoData(item);
        if (!fechaInicio) return false;

        if (startDate && fechaInicio < startDate) return false;
        if (endDate && fechaInicio > endDate) return false;

        return true;
      });
    }

    return sortByFechaInicioTurno(result);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setShowDateFilter(false);
  };

  const fetchCierreData = async () => {
    setLoading(true);
    setError(null);
    clearAllFilters();

    try {
      const result = await getCierreTurnoDataService();

      if (result.success) {
        const data = result.data || [];
        const sortedData = sortByFechaInicioTurno(data);
        setCierreData(sortedData);
        setFilteredData(sortedData);
        if (onError) onError(null);
      } else {
        const errorMsg = result.error || "Error al cargar datos";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || "Error al cargar datos de cierres";
      setError(errorMsg);
      if (onError) onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCierreData();
  }, [refreshFlag]);

  useEffect(() => {
    if (cierreData.length > 0) {
      const filtered = applyFilters(cierreData);
      setFilteredData(filtered);
    }
  }, [searchQuery, startDate, endDate]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const clearSearch = () => setSearchQuery("");
  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleViewDetails = (itemId) => {
    const item = filteredData.find((i) => i.id === itemId);
    if (item) {
      setSelectedItem(item);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSelectedItem(null);
    }, 300);
  };

  // ==================== NUEVA FUNCIÓN PARA GENERAR PDF ====================
  const handleGeneratePDF = async (item) => {
    if (!item) {
      setSnackbar({
        open: true,
        message: "No hay datos para generar el PDF",
        severity: "error",
      });
      return;
    }

    try {
      setDownloading((prev) => ({ ...prev, [item.id]: true }));

      const metadata = item.metadata || item.more || {};
      const balance = metadata.balance || {};
      const trans = metadata.trans || {};

      // Preparar los datos en el formato que espera generarPDFCierreTurno
      const formData = {
        efectivoInicial: Number(metadata.efectivoInicial || balance.EstContableAnterior || 0),
        recaudoEfectivo: Number(metadata.recaudoEfectivo || 0),
        usuarioNombre: item.user_kme || metadata.user_kme || "Explotador",
        numeroCaja: balance.NumeroCaja || "04",
        inicioTurno: balance.FHInicio,
        finTurno: balance.FHFin,
        fechaCierre: balance.FHFin,
      };

      // Preparar respuesta del backend en el formato que espera el generador
      const responseData = {
        data: {
          idSave: item.id,
          user_kme: item.user_kme || metadata.user_kme || "Explotador",
          balance: [balance],
          trans: trans,
        },
        idSave: item.id,
      };

      // Generar el PDF
      const result = await generarPDFCierreTurno({
        formData: formData,
        responseData: responseData,
      });

      if (result.success) {
        setSnackbar({
          open: true,
          message: `PDF generado exitosamente (ID: ${item.id})`,
          severity: "success",
        });
      } else {
        throw new Error(result.error || "Error al generar el PDF");
      }
    } catch (error) {
      console.error("Error generando PDF:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error al generar el PDF",
        severity: "error",
      });
    } finally {
      setDownloading((prev) => ({ ...prev, [item.id]: false }));
    }
  };

  // ==================== FUNCIÓN PARA DESCARGAR PDF ====================
  const handleDownloadPDF = async (itemId) => {
    if (!itemId) {
      setSnackbar({
        open: true,
        message: "ID no válido para generar PDF",
        severity: "error",
      });
      return;
    }

    // Buscar el item completo
    const item = filteredData.find((i) => i.id === itemId);
    if (!item) {
      setSnackbar({
        open: true,
        message: "No se encontraron datos para este ID",
        severity: "error",
      });
      return;
    }

    await handleGeneratePDF(item);
  };

  const renderIdCell = ({ value }) => <IdCell value={value} />;
  const renderUsuarioCell = ({ value }) => (
    <MDBox display="flex" alignItems="center" gap={0.5}>
      <Person fontSize="small" sx={{ color: "#64748b" }} />
      <MDTypography variant="button" fontWeight="medium" sx={{ color: "#1e293b" }}>
        {value}
      </MDTypography>
    </MDBox>
  );

  const columns = [
    { Header: "ID", accessor: "id", width: "5%", Cell: renderIdCell },
    { Header: "Usuario", accessor: "usuario", width: "8%", Cell: renderUsuarioCell },
    { Header: "Caja", accessor: "caja", width: "5%" },
    { Header: "Inicio Turno", accessor: "inicioTurno", width: "10%" },
    { Header: "Fin Turno", accessor: "finTurno", width: "10%" },
    { Header: "Duración", accessor: "duracion", width: "8%" },
    { Header: "Efectivo Inicial", accessor: "efectivoInicial", width: "8%" },
    { Header: "Recaudo Efectivo", accessor: "recaudoEfectivo", width: "8%" },
    { Header: "Efectivo Sistema", accessor: "efectivoSistema", width: "8%" },
    { Header: "Diferencia", accessor: "diferencia", width: "8%" },
    { Header: "Tarjeta Sistema", accessor: "tarjetaSistema", width: "8%" },
    { Header: "Total Sistema", accessor: "totalSistema", width: "8%" },
    { Header: "Acciones", accessor: "acciones", width: "7%" },
  ];

  const rows = filteredData.map((item) => {
    const metadata = item.metadata || item.more || {};
    const balance = metadata.balance || {};
    const trans = metadata.trans || {};

    const efectivoInicial = Number(metadata.efectivoInicial || balance.EstContableAnterior || 0);
    const recaudoEfectivoReportado = Number(metadata.recaudoEfectivo || 0);
    const efectivoSistema = Number(trans.valorE || 0);
    const tarjetaSistema = Number(trans.valorT || 0);
    const valorTotalSistema = efectivoSistema + tarjetaSistema;
    const diferenciaEfectivo = recaudoEfectivoReportado - efectivoSistema;

    const fechaInicioTurno = balance.FHInicio;
    const fechaFinTurno = balance.FHFin;
    const duracionTurno = calcularDuracion(fechaInicioTurno, fechaFinTurno);

    return {
      id: item.id,
      usuario: item.user_kme || metadata.user_kme || "N/A",
      caja: `Caja #${balance.NumeroCaja || "N/A"}`,
      inicioTurno: <FechaCell fecha={fechaInicioTurno} />,
      finTurno: <FechaCell fecha={fechaFinTurno} />,
      duracion: <DuracionCell duracion={duracionTurno} />,
      efectivoInicial: formatCurrency(efectivoInicial),
      recaudoEfectivo: formatCurrency(recaudoEfectivoReportado),
      efectivoSistema: formatCurrency(efectivoSistema),
      diferencia: (
        <MDTypography
          variant="caption"
          fontWeight="bold"
          sx={{ color: diferenciaEfectivo >= 0 ? "#2e7d32" : "#d32f2f" }}
        >
          {formatCurrency(diferenciaEfectivo)}
        </MDTypography>
      ),
      tarjetaSistema: formatCurrency(tarjetaSistema),
      totalSistema: (
        <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#1976d2" }}>
          {formatCurrency(valorTotalSistema)}
        </MDTypography>
      ),
      acciones: (
        <AccionesCell
          itemId={item.id}
          downloading={downloading[item.id]}
          onDownload={handleDownloadPDF}
          onViewDetails={handleViewDetails}
        />
      ),
    };
  });

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "cualquier fecha";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  return (
    <MDBox>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <DetailsModal
        open={modalOpen}
        item={selectedItem}
        onClose={handleCloseModal}
        downloading={selectedItem ? downloading[selectedItem.id] : false}
        onDownload={handleDownloadPDF}
      />

      <Card sx={{ mb: 3, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <MDBox p={2}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <MDBox>
              <MDTypography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#1e293b", display: "flex", alignItems: "center", gap: 1 }}
              >
                <AccountBalance sx={{ color: "#0b4f6c" }} />
                Historial de Cierres de Turno
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#64748b" }}>
                {filteredData.length} registros encontrados
              </MDTypography>
            </MDBox>

            <MDBox display="flex" gap={1} alignItems="center">
              <MDButton
                variant="outlined"
                onClick={() => setShowDateFilter(!showDateFilter)}
                startIcon={<FilterAlt />}
                size="small"
                sx={{
                  borderRadius: "6px",
                  borderColor: startDate || endDate ? "#0b4f6c" : "#cbd5e1",
                  color: startDate || endDate ? "#0b4f6c" : "#475569",
                }}
              >
                Fechas {(startDate || endDate) && "✓"}
              </MDButton>

              <MDButton
                variant="outlined"
                onClick={fetchCierreData}
                startIcon={<Refresh />}
                disabled={loading}
                size="small"
                sx={{
                  borderRadius: "6px",
                  borderColor: "#cbd5e1",
                  color: "#475569",
                }}
              >
                {loading ? "Cargando..." : "Actualizar"}
              </MDButton>

              <MDBox width="250px">
                <MDInput
                  fullWidth
                  placeholder="Buscar por usuario..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" sx={{ color: "#94a3b8" }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <Clear fontSize="small" sx={{ color: "#94a3b8" }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
            </MDBox>
          </MDBox>

          {showDateFilter && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#f8fafc",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
              }}
            >
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="subtitle2" sx={{ color: "#1e293b", fontWeight: 600 }}>
                  <CalendarToday sx={{ mr: 1, fontSize: 18, verticalAlign: "middle" }} />
                  Filtrar por Fecha de Inicio de Turno
                </MDTypography>
                {(startDate || endDate) && (
                  <MDButton
                    size="small"
                    onClick={clearDateFilters}
                    startIcon={<Clear fontSize="small" />}
                  >
                    Limpiar filtros
                  </MDButton>
                )}
              </MDBox>

              <Grid container spacing={2}>
                <Grid item xs={12} md={5}>
                  <MDInput
                    type="date"
                    label="Fecha desde"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <MDInput
                    type="date"
                    label="Fecha hasta"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDBox display="flex" height="100%" alignItems="center">
                    <Chip
                      label={`${filteredData.length} registros`}
                      size="small"
                      sx={{ bgcolor: "#e2e8f0", color: "#1e293b", fontWeight: "medium" }}
                    />
                  </MDBox>
                </Grid>
              </Grid>

              {(startDate || endDate) && (
                <MDBox mt={2}>
                  <MDTypography variant="caption" sx={{ color: "#64748b" }}>
                    Mostrando turnos iniciados entre{" "}
                    <strong>{formatDateForDisplay(startDate) || "cualquier fecha"}</strong> y{" "}
                    <strong>{formatDateForDisplay(endDate) || "cualquier fecha"}</strong>
                  </MDTypography>
                </MDBox>
              )}
            </Box>
          )}
        </MDBox>
      </Card>

      {loading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress sx={{ color: "#0b4f6c" }} />
        </MDBox>
      ) : error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <MDButton color="inherit" size="small" onClick={fetchCierreData}>
              Reintentar
            </MDButton>
          }
        >
          {error}
        </Alert>
      ) : filteredData.length === 0 ? (
        <MDBox p={3} textAlign="center">
          <Search sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
          <MDTypography variant="h6" sx={{ color: "#64748b" }}>
            {searchQuery || startDate || endDate
              ? "No se encontraron resultados con los filtros aplicados"
              : "No hay cierres registrados"}
          </MDTypography>
          {(searchQuery || startDate || endDate) && (
            <MDBox mt={2} display="flex" gap={2} justifyContent="center">
              {searchQuery && (
                <MDButton variant="outlined" onClick={clearSearch}>
                  Limpiar búsqueda
                </MDButton>
              )}
              {(startDate || endDate) && (
                <MDButton variant="outlined" onClick={clearDateFilters}>
                  Limpiar filtros de fecha
                </MDButton>
              )}
            </MDBox>
          )}
        </MDBox>
      ) : (
        <DataTable
          table={{ columns, rows }}
          isSorted={true}
          entriesPerPage={10}
          showTotalEntries={true}
          noEndBorder
          canSearch={false}
        />
      )}
    </MDBox>
  );
}

TableCameContent.propTypes = {
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
};

TableCameContent.defaultProps = {
  refreshFlag: false,
  onError: null,
};

export default TableCameContent;
